import { getFormProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { Block, Button, f7, Icon, List, ListInput } from "framework7-react";
import { z as zod } from "zod";

import type { UpdateAccountBodyOneOfTwo } from "@/generated/api";
import { useUpdateAccount } from "@/generated/api";
import { useRefetchCurrentUser } from "@/hooks/useRefetchCurrentUser";
import { useTokenStore } from "@/stores/tokenStore";

type ChangePasswordFormProps = {
  onCompleted: VoidFunction;
};

export function ChangePasswordForm({ onCompleted }: ChangePasswordFormProps) {
  const { setToken } = useTokenStore();

  const { refetch } = useRefetchCurrentUser();

  const { mutate, isPending } = useUpdateAccount();

  const passwordSchema = zod.object({
    current_password: zod.string(),
    password: zod.string(),
    password_confirmation: zod.string(),
  });

  const [form, fields] = useForm<UpdateAccountBodyOneOfTwo>({
    defaultValue: {
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    onSubmit(event, { formData }) {
      event.preventDefault();
      const submission = parseWithZod(formData, { schema: passwordSchema });

      if (submission.status !== "success") {
        return submission.reply();
      }

      mutate(
        { data: submission.value },
        {
          onError: () => {
            f7.toast.create({
              closeTimeout: 2000,
              text: "パスワードの更新に失敗しました: ",
            });
          },
          onSuccess: (response) => {
            if (response.status === 200) {
              const token = (response.headers["access-token"] as string | undefined) ?? "";
              const client = (response.headers["client"] as string | undefined) ?? "";
              const uid = (response.headers["uid"] as string | undefined) ?? "";
              setToken({ client, token, uid });
              refetch().finally(onCompleted);
            }
          },
        },
      );
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: passwordSchema });
    },
    shouldValidate: "onBlur",
  });

  return (
    <form {...getFormProps(form)}>
      <List>
        <ListInput
          clearButton
          floatingLabel
          required
          validate
          autocomplete="current-password"
          info="パスワードを入力してください"
          label="パスワード"
          name={fields.current_password.name}
          placeholder="password@123456"
          type="password"
        >
          <Icon
            material="key"
            slot="media"
          />
        </ListInput>

        <ListInput
          clearButton
          floatingLabel
          required
          validate
          autocomplete="new-password"
          info="パスワードを入力してください"
          label="パスワード"
          name={fields.password.name}
          placeholder="password@123456"
          type="password"
        >
          <Icon
            material="key"
            slot="media"
          />
        </ListInput>

        <ListInput
          clearButton
          floatingLabel
          required
          validate
          info="パスワードを入力してください"
          label="パスワード (確認用)"
          name={fields.password_confirmation.name}
          placeholder="password@123456"
          type="password"
        >
          <Icon
            material="key"
            slot="media"
          />
        </ListInput>
      </List>
      <Block>
        <Button
          fill
          large
          loading={isPending}
          type="submit"
        >
          パスワード更新
        </Button>
      </Block>
    </form>
  );
}
